/**
 * Per-product aspect-ratio memory (iter 82).
 *
 * The aspect ratio chosen on the Photo Studio output settings popover is
 * mostly a per-product preference: an apron product wants 4:3 portrait,
 * a banner wants 16:9 wide. Asking the user to re-pick on every product
 * switch is friction; persisting the choice removes it.
 *
 * Stored in `localStorage` under `studio.aspect.<productUid>` so the
 * memory is browser-local and per-product. Stale entries are cleared
 * lazily — if a product is deleted, its entry just sits inert until
 * cleared manually.
 *
 * Schema: a JSON-stringified string (the aspect-ratio token). We
 * intentionally don't expand to an object to keep the surface small.
 */

import type { AspectRatio } from '@studio/contract';

const KEY_PREFIX = 'studio.aspect.';

/** Read the remembered aspect ratio for a product, or null if none stored. */
export function readAspectRatio(productUid: string): AspectRatio | null {
  if (typeof window === 'undefined' || !productUid) return null;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + productUid);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'string') return null;
    return parsed as AspectRatio;
  } catch {
    return null;
  }
}

/** Persist the aspect ratio for a product. No-op when running outside a browser. */
export function writeAspectRatio(productUid: string, ratio: AspectRatio): void {
  if (typeof window === 'undefined' || !productUid) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + productUid, JSON.stringify(ratio));
  } catch {
    // localStorage can throw in private-mode browsers / over quota.
    // Silently drop — the next session will start fresh, which is fine.
  }
}

/** Clear the remembered ratio for one product. */
export function clearAspectRatio(productUid: string): void {
  if (typeof window === 'undefined' || !productUid) return;
  try {
    window.localStorage.removeItem(KEY_PREFIX + productUid);
  } catch {
    // ignore
  }
}
